import * as Api from '@ViewModels';

export enum RegEdFieldType {
	String = 'String',
	Text = 'Text',
	Integer = 'Integer',
	Decimal = 'Decimal',
	Boolean = 'Boolean',
	Money = 'Money',
	Date = 'Date',
	DateTime = 'DateTime',
	Time = 'Time',
	Email = 'Email',
	User = 'User',
	Contact = 'Contact',
	Option = 'Option',
	MultipleOptions = 'MultipleOptions',
	FullAddress = 'FullAddress',
	Note = 'Note',
	MeetingScheduler = 'MeetingScheduler',
	FileUpload = 'FileUpload',
	Label = 'Label',
}

export interface IRegEdMetadataOption {
	label?: string;
	externalId?: string;
	isActive?: boolean;
}

export interface IRegEdMetadataField {
	id?: string;
	externalId?: string;
	label?: string;
	helpText?: string;
	fieldType?: RegEdFieldType;
	isRequired?: boolean;
	isActive?: boolean;
	options?: IRegEdMetadataOption[];
	/** The polymorphic `_type`-discriminated `QuestionVisibility`; absent when the field is always shown. */
	visibleWhen?: Record<string, unknown> | null;
	order?: number;
	/**
	 * `yyyy-MM-dd`, set only on the requested-review-date question and computed from the advisor's own
	 * calendar day. A date on or after it cannot be rejected for lead time. Null means no window —
	 * don't infer a default.
	 */
	minimumDate?: string | null;
	/**
	 * Answers the server has already resolved for this field, each already the exact value a selected
	 * option submits under — echo them back verbatim rather than matching on them. Absent wherever the
	 * answer could not be established, since a preselection reaches the reviewer as an assertion.
	 */
	defaultValues?: string[] | null;
}

export interface IRegEdMetadataForm {
	format?: string;
	displayName?: string;
	fields?: IRegEdMetadataField[];
}

export interface IRegEdMetadataFormImportResult {
	formsWritten?: number;
	formsFailed?: number;
	questionsImported?: number;
	optionsImported?: number;
	warnings?: string[];
}

export interface IRegEdBrokerDealerSummary {
	id?: string;
	name?: string;
}

export enum RegEdConfigurationRequirement {
	Unknown = 'Unknown',
	BrokerDealer = 'BrokerDealer',
}

export interface IRegEdConfigurationStatus {
	needsConfiguration?: boolean;
	missingRequirements?: RegEdConfigurationRequirement[];
}

export interface IRegEdIntegrationStatus {
	id?: string;
	brokerDealerId?: string;
	/**
	 * Kill-switch: configured ≠ enabled. The account routes compliance through RegEd only when this is
	 * `true` AND `needsConfiguration` is `false`; only the enable/disable route flips it.
	 */
	enabled?: boolean;
	/**
	 * When true the broker-dealer's compliance officer files through the tokenised review portal, so the
	 * advisor never fills the questionnaire. Absent means false; the API refuses to turn it on while
	 * `enabled` is false, since the portal's own guard requires both.
	 */
	officerFilesSubmissions?: boolean;
	/**
	 * AdTrax identity a portal filing is submitted under. Null when unset, which is a real
	 * configuration and not a misconfiguration — the officer then files under the advisor's own id.
	 */
	officerRegEdUserId?: string | null;
	brokerDealerName?: string | null;
	/**
	 * Days the broker-dealer needs between filing and the requested review date; 0 when it sets none.
	 * Counted from the day the answers are filed, which on a handover is after scheduling.
	 */
	reviewLeadTimeDays?: number;
	configurationStatus?: IRegEdConfigurationStatus;
	connectedAt?: string;
	connectedByUserId?: string;
}

export interface IRegEdConnectionTestResult {
	brokerDealerId?: string;
	success?: boolean;
	message?: string;
	testedAt?: string;
}

/** `answers` is keyed by `RegEdMetadataField.id`. */
export interface IRegEdSubmissionMetadata {
	format: string;
	answers: Record<string, unknown>;
}

export const buildRegEdSubmissionMetadata = (
	format: string,
	answers: Record<string, unknown>
): IRegEdSubmissionMetadata => ({ format, answers });

export enum RegEdComplianceContentType {
	Unknown = 'Unknown',
	Email = 'Email',
	Blog = 'Blog',
	SocialMediaPost = 'SocialMediaPost',
}

/** The fields of a create that can be checked before the parent exists. */
export interface IRegEdSubmissionValidateRequest {
	metadata: IRegEdSubmissionMetadata;
	revisesApprovalRequestId?: string | null;
	reviewLeadTimeOverrideReason?: string;
	/** Whose RegEd mapping files it when sending on someone's behalf; the server defaults to the caller. */
	ownerUserId?: string;
}

/** `POST compliance/regEd/submissions/defer` — leaves the filing to the parent's owner. */
export interface IRegEdSubmissionDeferRequest {
	parentType: RegEdComplianceContentType;
	parentId: string;
	approvalRequestId: string;
	revisesApprovalRequestId: string | null;
}

export interface IRegEdSubmissionCreateRequest {
	parentType: RegEdComplianceContentType;
	parentId: string;
	approvalRequestId: string;
	metadata: IRegEdSubmissionMetadata;
	revisesApprovalRequestId?: string | null;
	/**
	 * Only when the requested review date is inside the firm's notice window and the advisor has
	 * explained why. The reviewer reads it. Sending it otherwise records a rush that never happened.
	 */
	reviewLeadTimeOverrideReason?: string;
}

/** The broker-dealer's review notice window, carried from a handover to the scheduler that states it. */
export interface IRegEdReviewNotice {
	brokerDealerName?: string | null;
	days: number;
}

/**
 * What the compliance prompt hands back. The reason rides alongside the answers so the flows that
 * stash this between the form and the submission carry it without each needing to know about it;
 * `submitRegEdCompliance` splits it back out onto the request body.
 */
export interface IRegEdSubmissionInput extends IRegEdSubmissionMetadata {
	reviewLeadTimeOverrideReason?: string;
	/** Set on a handover, so the scheduler can explain its floor. Never reaches the wire. */
	reviewNotice?: IRegEdReviewNotice;
	/**
	 * Floors the send date in the schedulers. Already clamped to today or later by the form — a review
	 * date that has passed imposes no floor, and handing a past date to a scheduler lowers its floor
	 * instead of failing to bind. Never reaches the wire.
	 */
	earliestSendDate?: string;
	/**
	 * Hand the filing to the account's user instead of making it. Never reaches the wire: it routes the
	 * call to the deferral endpoint, and rides on the metadata so it passes through every host unchanged.
	 */
	deferToAccountUser?: boolean;
}

/** `category` on the envelope's `error`; unrelated 400s carry no error object at all. */
export const REGED_COMPLIANCE_ERROR_CATEGORY = 'RegEdComplianceErrorCode';

/** Server-owned and append-only — never renumbered. */
export enum RegEdComplianceErrorCode {
	ReviewLeadTimeTooSoon = 1,
	RequestedReviewDateMissing = 2,
	ReviewLeadTimeOverrideReasonTooLong = 3,
	FirstUseDateMismatch = 4,
	ReviewDateAfterFirstUse = 5,
	/** The message names each question to re-answer — required but blank, a retired option, an unreadable date. */
	AnswersInvalid = 6,
	OwnerNotMapped = 8,
	/** The round being revised can no longer take a revision, or has no submission. */
	RevisionNotAllowed = 9,
	/**
	 * An admin from outside the account tried to file. The API has already recorded the piece as waiting
	 * on the account's user, so this is a handover, not a failure.
	 */
	FilingRequiresAccountUser = 10,
	/**
	 * The round is with the firm's internal compliance reviewer, raised before the account moved to RegEd, so the API
	 * filed and deferred nothing. Not a failure: the piece waits on that reviewer.
	 */
	ReviewedInternally = 11,
}

export const REVIEWED_INTERNALLY_MESSAGE =
	"It's with your firm's internal compliance reviewer, so nothing was filed with RegEd.";

export const isRegEdComplianceErrorCode = (
	error: { category?: string; errorCode?: number } | null | undefined,
	expected: RegEdComplianceErrorCode
): boolean => error?.category === REGED_COMPLIANCE_ERROR_CATEGORY && error?.errorCode === expected;

export const REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH = 1000;

/** A reason resolves the rejection; a missing review date has to be answered in the questionnaire. */
export const isRegEdLeadTimeReasonFixable = (errorCode: RegEdComplianceErrorCode | undefined): boolean =>
	errorCode === RegEdComplianceErrorCode.ReviewLeadTimeTooSoon ||
	errorCode === RegEdComplianceErrorCode.ReviewLeadTimeOverrideReasonTooLong;

/** The compliance error code on a failed operation result, or undefined when it failed for another reason. */
export const getRegEdComplianceErrorCode = (error: unknown): RegEdComplianceErrorCode | undefined => {
	const technicalError = (error as { error?: { category?: string; errorCode?: number } } | null)?.error;
	return technicalError?.category === REGED_COMPLIANCE_ERROR_CATEGORY
		? (technicalError.errorCode as RegEdComplianceErrorCode)
		: undefined;
};

/** Recorded when a submission was granted inside the notice window; null when the window was met. */
export interface IRegEdReviewLeadTimeOverride {
	reason?: string;
	requestedReviewDate?: string;
	/** Snapshotted, so a historical record still reads correctly if the firm's window changes. */
	requiredLeadTimeDays?: number;
	byUserId?: string;
	at?: string;
}

/** Firm-level content Levitate files on the broker-dealer's behalf, so its advisors needn't file it individually. */
export const REGED_VENDOR_SUBMISSION_FORMAT = 'Vendor Submission';

/**
 * Formats a Levitate admin files on the firm's behalf, from the admin app rather than by an advisor.
 * Presentational only for now — the submissions endpoint accepts any format in the broker-dealer's
 * catalog regardless of caller, so this cannot be the enforcement point.
 */
export const REGED_ADMIN_ONLY_FORMATS = [REGED_VENDOR_SUBMISSION_FORMAT];

export const isRegEdAdvisorSelectableFormat = (format: string): boolean =>
	!REGED_ADMIN_ONLY_FORMATS.some(adminOnly => adminOnly.toLowerCase() === format?.trim().toLowerCase());

export interface IRegEdUserMap {
	id?: string;
	levitateUserId?: string;
	regEdUserId?: string;
}

export interface IRegEdUserMapUpsertRequest {
	regEdUserId: string;
}

/** The UI calls this a Rep Code; the wire field is `regEdUserId`. AdTrax caps it at 24. */
export const REGED_USER_ID_MAX_LENGTH = 24;

export enum RegEdSubmissionStatus {
	Pending = 'Pending',
	Submitting = 'Submitting',
	Approved = 'Approved',
	Rejected = 'Rejected',
	Withdrawn = 'Withdrawn',
	/** Never filed. With a `failureReason` its approval request is rejected so the advisor can resend; without one it needs an operator. */
	Failed = 'Failed',
	/** Network failure mid-submit — may or may not have reached AdTrax. */
	Unknown = 'Unknown',
	/** No AdTrax decision within the SLA window. */
	Expired = 'Expired',
}

export enum RegEdSubmissionFailureReason {
	/** AdTrax refused the filing itself — the advisor can edit and resend. */
	ProviderRejected = 'ProviderRejected',
	/** RegEd refused the submitter's access to this filing; an administrator has to fix their mapping or permissions. */
	ProviderUnauthorized = 'ProviderUnauthorized',
	/** An administrator has to map the submitter to a RegEd user before a resend can succeed. */
	SubmitterNotMapped = 'SubmitterNotMapped',
	/**
	 * A placeholder, not a filing: the content is scheduled and held, but its answers were refused before
	 * anything was filed, and its approval request was not rejected. Finished by re-posting the answers for
	 * the same approval request, then regenerating the compliance PDF.
	 */
	NotSubmitted = 'NotSubmitted',
}

export const isRegEdNotSubmitted = (submission: IRegEdSubmissionView | null | undefined): boolean =>
	submission?.status === RegEdSubmissionStatus.Failed &&
	submission.failureReason === RegEdSubmissionFailureReason.NotSubmitted;

/** A placeholder an admin left for the account's user, as opposed to one a refused filing left behind. */
export const isRegEdDeferredToAccountUser = (submission: IRegEdSubmissionView | null | undefined): boolean =>
	isRegEdNotSubmitted(submission) && !!submission?.deferredDate;

/**
 * Submissions are filed under the advisor's AdTrax identity, so an admin acting from outside the
 * account must not answer the questionnaire. Reads the plain context shape, which every caller has —
 * a view model's `isValid` is absent on it and would read as false.
 */
export const isRegEdAdminSession = (impersonationContext: { account?: { id?: string } } | null | undefined): boolean =>
	!!impersonationContext?.account?.id;

/** What an admin's prompt hands back in place of answers. */
export const REGED_DEFERRED_SUBMISSION: IRegEdSubmissionInput = { answers: {}, deferToAccountUser: true, format: '' };

/**
 * RegEd refused the filing and its approval request was rejected back to the advisor. Legacy failures
 * without a reason were never rejected back, so the revise endpoint would refuse to reopen them.
 */
export const isRegEdResendableFailure = (submission: IRegEdSubmissionView | null | undefined): boolean =>
	submission?.status === RegEdSubmissionStatus.Failed && !!submission.failureReason && !isRegEdNotSubmitted(submission);

/**
 * The round a filing of `submission`'s answers chains to. A refused first filing never became an AdTrax
 * item, so its resend is fresh; a refused revision's item is still open on its tracking number, so its
 * resend revises it. A placeholder carries the round its own refused create was revising.
 */
export const getRegEdRevisesApprovalRequestId = (
	submission: IRegEdSubmissionView | null | undefined
): string | null => {
	if (isRegEdNotSubmitted(submission)) {
		return submission?.revisesApprovalRequestId ?? null;
	}
	if (isRegEdResendableFailure(submission) && !submission?.trackingNumber) {
		return null;
	}
	return submission?.approvalRequestId ?? null;
};

export enum RegEdApprovalStatus {
	Rejected = 'Rejected',
	Approved = 'Approved',
}

/** AdTrax decision code that gates the revise-and-resubmit flow (see `decisionCode`). */
export const REGED_REVISABLE_DECISION_CODE = 'Revise and Resubmit';

/** Approved, but the piece is HELD until the advisor completes the reviewer's condition and releases it. */
export const REGED_CONDITIONALLY_APPROVED_DECISION_CODE = 'Conditionally Approved';

/** Hard reject — terminal; the revise flow must NOT be offered (the server 400s a revision). */
export const REGED_DECLINED_DECISION_CODE = 'Declined';

/**
 * AdTrax pads some decision codes (observed `"Conditionally Approved "`) and rows cached before the
 * server-side canonicalization keep the padding — there is no backfill, so always compare trimmed and
 * case-insensitively or a historical row silently misses its gate.
 */
export const isRegEdDecisionCode = (decisionCode: string | null | undefined, expected: string): boolean =>
	decisionCode?.trim().toLowerCase() === expected.trim().toLowerCase();

/**
 * Determinate only once a response exists — a pending AR has `response == null` and must read as
 * "unknown", never "released", or a held piece would hide its release action and silently miss its send.
 */
export const getRegEdConditionsPending = (
	approvalRequests: Array<{ response?: { conditionsPending?: boolean } | null }> | null | undefined
): boolean | undefined =>
	approvalRequests?.some(x => !!x.response)
		? approvalRequests.some(x => x.response?.conditionsPending === true)
		: undefined;

export const findNewestRound = <T extends { creationDate?: string }>(
	approvalRequests: T[] | null | undefined
): T | undefined =>
	[...(approvalRequests ?? [])]
		// Coalesced rather than filtered: a round with no creationDate would otherwise sort by NaN, which
		// is implementation-defined. Sorting it oldest keeps it selectable when it is the only one left,
		// where dropping it would leave the caller with nothing to act on.
		.sort(
			(a, b) =>
				(b.creationDate ? new Date(b.creationDate).valueOf() : 0) -
				(a.creationDate ? new Date(a.creationDate).valueOf() : 0)
		)[0];

/**
 * The round `revise` just raised, resolved by recency among the unanswered rounds rather than index 0.
 * Superseded rounds are filtered out of every response, but a co-approver's own unanswered round stays
 * live and can precede the new one in the array — so position says nothing about which is the revision.
 */
export const findRaisedRegEdRound = <T extends { creationDate?: string; id?: string; response?: unknown }>(
	approvalRequests: T[] | null | undefined
): T | undefined => findNewestRound((approvalRequests ?? []).filter(request => !request.response));

/**
 * Whether one piece's review UI is RegEd's. A round raised for the internal approver before the account
 * moved to RegEd is still theirs, so its approve actions must stay. Off RegEd everything is internal,
 * matching the API, which hands an unfiled round back to the internal approver when RegEd is switched off.
 */
export const reviewsThroughRegEd = (
	accountUsesRegEd: boolean,
	approvalRequests: Api.ICampaignApprovalRequest[] | null | undefined
): boolean => {
	if (!accountUsesRegEd) {
		return false;
	}
	const current = findRaisedRegEdRound(approvalRequests) ?? findNewestRound(approvalRequests);
	return current?.complianceProvider !== Api.ComplianceProvider.Internal;
};

/**
 * Whether the piece's open round is with the firm's internal reviewer. Editing it keeps that reviewer's round, so
 * there is nothing to ask for or file with RegEd: the API would refuse it as `ReviewedInternally`.
 */
export const holdsOpenInternalRound = (approvalRequests: Api.ICampaignApprovalRequest[] | null | undefined): boolean =>
	findRaisedRegEdRound(approvalRequests)?.complianceProvider === Api.ComplianceProvider.Internal;

/** Reviewer's actionable text for a decided submission — the condition to meet or the reason to revise. */
export interface IRegEdReviewNote {
	noteText?: string;
	addedByName?: string;
	addedByEmail?: string;
	addedOn?: string | null;
}

/**
 * Why a decided piece is back with the advisor. Resolved server-side from the broker-dealer's
 * decision-code configuration, so switch on this rather than reconstructing intent from the content
 * status — that differs per type (email holds as `WaitingForApproval`, blog/post as `Pending`, and a
 * revision request looks identical to a hard reject). A hard reject never appears here.
 */
export enum RegEdActionReason {
	RevisionRequested = 'RevisionRequested',
	ConditionsPending = 'ConditionsPending',
	/** Never reached a reviewer — `failureReason` says why. Resent as a fresh submission, not a revision. */
	SubmissionFailed = 'SubmissionFailed',
}

/** Which email report a send appears under. Absent for blogs and social posts. */
export enum RegEdEmailKind {
	Individual = 'Individual',
	Campaign = 'Campaign',
	Automation = 'Automation',
}

/** One piece whose compliance decision landed but that still owes the advisor something. */
export interface IRegEdActionNeededItem {
	reason?: RegEdActionReason;
	/** Names the tab the piece is under, so a link can land on the list that actually holds it. */
	emailKind?: RegEdEmailKind | null;
	contentType?: RegEdComplianceContentType;
	parentId?: string;
	approvalRequestId?: string;
	submissionId?: string;
	trackingNumber?: string;
	/** Raw AdTrax code, for display only — `reason` is the field to branch on. */
	decisionCode?: string;
	title?: string;
	/** The piece's own send date; routinely in the past, since a decision can land after it. */
	scheduledDate?: string | null;
	reviewNotes?: IRegEdReviewNote[];
	/** Set only for `SubmissionFailed`. */
	failureReason?: RegEdSubmissionFailureReason | null;
	/** The round a `NotSubmitted` revision was revising, to send back with the retry. */
	revisesApprovalRequestId?: string | null;
	/** Set when an admin scheduled the piece and left its filing to the account's user. */
	deferredDate?: string | null;
	/**
	 * The piece's own status as its report filters on it — the computed `EmailSendStatus` for email. A filter
	 * value only; `reason` says what the advisor owes.
	 */
	parentStatus?: string | null;
}

export interface IRegEdSubmissionAnnotation {
	id?: string;
	fileName?: string;
	mimeType?: string;
}

/** Rows are submission *chains* (a revision repoints the same row), returned newest-first. */
export interface IRegEdSubmissionView {
	id?: string;
	creationDate?: string;
	lastModifiedDate?: string;
	approvalRequestId?: string;
	revisesApprovalRequestId?: string | null;
	contentType?: RegEdComplianceContentType;
	parentId?: string;
	brokerDealerId?: string;
	status?: RegEdSubmissionStatus;
	approvalStatus?: RegEdApprovalStatus | null;
	trackingNumber?: string | null;
	decisionCode?: string | null;
	failureReason?: RegEdSubmissionFailureReason | null;
	/** Set when an admin scheduled the piece and left its filing to the account's user. */
	deferredDate?: string | null;
	metadata?: IRegEdSubmissionMetadata;
	/** Absent until the API exposes them; `[]` when the reviewer left none. */
	reviewNotes?: IRegEdReviewNote[];
	/** Reviewer PDFs for a decided submission; `[]` when none exist. */
	annotations?: IRegEdSubmissionAnnotation[];
	/** Present only when the submission was granted inside the firm's notice window. */
	reviewLeadTimeOverride?: IRegEdReviewLeadTimeOverride | null;
}
